import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { ChartTypeNames, ChartType } from '../types'
import { 
  SparklesIcon, 
  TrashIcon, 
  ArrowTopRightOnSquareIcon,
  UserCircleIcon,
  CalendarIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  UsersIcon,
  StarIcon,
  ClockIcon
} from '@heroicons/react/24/outline'

interface Chart {
  id: number | string
  name: string
  chart_type: ChartType
  birth_date: string
  birth_time: string
  birth_location: string
  person2_name?: string
  created_at: string
  isFavorite?: boolean
}

export default function UserCenter() {
  const navigate = useNavigate()
  const { user, token, logout, isAuthenticated } = useAuthStore()
  const [charts, setCharts] = useState<Chart[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<ChartType | 'all'>('all')
  const [searchQuery, setSearchQuery] = useState('')

  // 从 localStorage 加载临时星盘
  useEffect(() => {
    if (!isAuthenticated) {
      // 加载未登录时的临时星盘
      const tempCharts: Chart[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key?.startsWith('chart_temp_')) {
          try {
            const data = JSON.parse(localStorage.getItem(key) || '{}')
            tempCharts.push({
              id: data.id,
              name: data.name,
              chart_type: data.chart_type || 'natal',
              birth_date: data.birth_date,
              birth_time: data.birth_time,
              birth_location: data.birth_location,
              person2_name: data.person2_name,
              created_at: data.createdAt,
            })
          } catch {}
        }
      }
      setCharts(tempCharts.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ))
      setLoading(false)
      return
    }

    // 登录后从 API 获取
    fetchCharts()
  }, [isAuthenticated])

  const fetchCharts = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/charts`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await response.json()
      if (data.success) {
        setCharts(data.data)
      }
    } catch (error) {
      console.error('Failed to fetch charts:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: number | string) => {
    if (!confirm('确定要删除这个星盘吗？')) return

    if (typeof id === 'string' && id.startsWith('temp_')) {
      localStorage.removeItem(`chart_${id}`)
      setCharts(charts.filter(c => c.id !== id))
      return
    }

    if (!isAuthenticated) return

    try {
      await fetch(`${import.meta.env.VITE_API_URL}/api/charts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      setCharts(charts.filter(c => c.id !== id))
    } catch (error) {
      console.error('Failed to delete chart:', error)
    }
  }

  const filteredCharts = charts.filter(chart => {
    if (filter !== 'all' && chart.chart_type !== filter) return false
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      return chart.name.toLowerCase().includes(query) ||
             chart.birth_location.toLowerCase().includes(query)
    }
    return true
  })

  const stats = {
    total: charts.length,
    natal: charts.filter(c => c.chart_type === 'natal').length,
    synastry: charts.filter(c => ['synastry', 'composite', 'davison'].includes(c.chart_type)).length,
    transit: charts.filter(c => ['transit', 'progressed', 'solar_arc', 'tertiary'].includes(c.chart_type)).length,
  }

  if (!isAuthenticated && charts.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-cyber-blue/10 flex items-center justify-center">
            <UserCircleIcon className="w-10 h-10 text-cyber-blue" />
          </div>
          <h2 className="text-2xl font-cyber text-white mb-4">用户中心</h2>
          <p className="text-gray-400 mb-6">
            登录后可以永久保存星盘、查看历史记录、生成 AI 解读报告
          </p>
          <button 
            onClick={() => {
              const authUrl = `https://auth.7e.ink/oauth/authorize?client_id=astro&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/callback')}&response_type=code`
              window.location.href = authUrl
            }}
            className="cyber-button"
          >
            登录 / 注册
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-4">
      <div className="max-w-6xl mx-auto">
        {/* 用户信息卡 */}
        <div className="cyber-card p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-cyber-blue to-cyber-purple p-0.5">
                  <div className="w-full h-full rounded-2xl bg-cyber-dark flex items-center justify-center text-3xl overflow-hidden">
                    {user?.avatar_url ? (
                      <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      user?.display_name?.[0] || user?.username?.[0] || '👤'
                    )}
                  </div>
                </div>
                {isAuthenticated && (
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-cyber-green border-2 border-cyber-black flex items-center justify-center">
                    <span className="text-xs">✓</span>
                  </div>
                )}
              </div>
              <div>
                <h1 className="text-2xl font-cyber text-white">
                  {user?.display_name || user?.username || user?.email || '访客用户'}
                </h1>
                <p className="text-gray-400 text-sm">{user?.email || '未登录'}</p>
                {!isAuthenticated && (
                  <p className="text-cyber-yellow text-xs mt-1">
                    ⚠ 当前为临时会话，星盘将在24小时后清除
                  </p>
                )}
              </div>
            </div>

            {/* 统计 */}
            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 rounded-lg bg-cyber-dark/50 text-center">
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-xs text-gray-500">总星盘</p>
              </div>
              <div className="p-3 rounded-lg bg-cyber-dark/50 text-center">
                <p className="text-2xl font-bold text-cyber-blue">{stats.natal}</p>
                <p className="text-xs text-gray-500">本命盘</p>
              </div>
              <div className="p-3 rounded-lg bg-cyber-dark/50 text-center">
                <p className="text-2xl font-bold text-cyber-pink">{stats.synastry}</p>
                <p className="text-xs text-gray-500">合盘</p>
              </div>
              <div className="p-3 rounded-lg bg-cyber-dark/50 text-center">
                <p className="text-2xl font-bold text-cyber-purple">{stats.transit}</p>
                <p className="text-xs text-gray-500">推运</p>
              </div>
            </div>

            {isAuthenticated && (
              <button
                onClick={logout}
                className="cyber-button-outline text-sm"
              >
                退出登录
              </button>
            )}
          </div>
        </div>

        {/* 操作栏 */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          {/* 搜索 */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索星盘..."
              className="w-full pl-10 pr-4 py-3 bg-cyber-dark border border-cyber-blue/20 rounded-lg text-white placeholder-gray-500 focus:border-cyber-blue focus:outline-none"
            />
          </div>

          {/* 筛选 */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-5 h-5 text-gray-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as ChartType | 'all')}
              className="px-4 py-3 bg-cyber-dark border border-cyber-blue/20 rounded-lg text-white focus:border-cyber-blue focus:outline-none"
            >
              <option value="all">全部类型</option>
              <option value="natal">本命盘</option>
              <option value="synastry">比较盘</option>
              <option value="composite">组合盘</option>
              <option value="davison">时空盘</option>
              <option value="transit">行运盘</option>
              <option value="progressed">次限盘</option>
              <option value="solar_arc">太阳弧</option>
              <option value="tertiary">三限盘</option>
            </select>
          </div>

          {/* 新建按钮 */}
          <button
            onClick={() => navigate('/chart')}
            className="cyber-button flex items-center gap-2"
          >
            <PlusIcon className="w-5 h-5" />
            新建星盘
          </button>
        </div>

        {/* 星盘列表 */}
        <div className="cyber-card p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="cyber-loader mx-auto" />
            </div>
          ) : filteredCharts.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cyber-dark flex items-center justify-center">
                <StarIcon className="w-8 h-8 text-gray-500" />
              </div>
              <p className="text-gray-400 mb-4">
                {searchQuery ? '没有找到匹配的星盘' : '还没有星盘'}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => navigate('/chart')}
                  className="cyber-button"
                >
                  <SparklesIcon className="w-5 h-5 inline mr-2" />
                  创建第一个星盘
                </button>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {filteredCharts.map((chart) => {
                const typeInfo = ChartTypeNames[chart.chart_type]
                const isTemp = typeof chart.id === 'string' && chart.id.startsWith('temp_')
                
                return (
                  <div
                    key={chart.id}
                    className="group p-4 bg-cyber-dark/50 rounded-xl border border-cyber-blue/10 hover:border-cyber-blue/30 transition-all"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{typeInfo.icon}</span>
                        <div>
                          <h3 className="font-cyber text-white group-hover:text-cyber-blue transition-colors">
                            {chart.name}
                          </h3>
                          <span className="text-xs text-gray-500">{typeInfo.name}</span>
                        </div>
                      </div>
                      {isTemp && (
                        <span className="px-2 py-0.5 rounded text-[10px] bg-cyber-yellow/10 text-cyber-yellow border border-cyber-yellow/20">
                          临时
                        </span>
                      )}
                    </div>

                    <div className="space-y-2 text-sm mb-4">
                      <div className="flex items-center gap-2 text-gray-400">
                        <CalendarIcon className="w-4 h-4" />
                        <span>{chart.birth_date} {chart.birth_time}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-400">
                        <ClockIcon className="w-4 h-4" />
                        <span className="truncate">{chart.birth_location}</span>
                      </div>
                      {chart.person2_name && (
                        <div className="flex items-center gap-2 text-cyber-pink">
                          <UsersIcon className="w-4 h-4" />
                          <span>+ {chart.person2_name}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        {new Date(chart.created_at).toLocaleDateString()}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/chart/${chart.id}`)}
                          className="p-2 text-cyber-blue hover:bg-cyber-blue/10 rounded-lg transition-colors"
                          title="查看"
                        >
                          <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(chart.id)}
                          className="p-2 text-cyber-pink hover:bg-cyber-pink/10 rounded-lg transition-colors"
                          title="删除"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
